import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  Facebook,
  Flag,
  Heart,
  House,
  Info,
  Instagram,
  Linkedin,
  MapPin,
  Monitor,
  Share2,
  Sparkles,
  Users,
  X,
  Youtube,
} from "lucide-react";
import { GLOBAL_IMAGES } from "../assets/asset";
import { getTutorById, TUTORS } from "./tutorInfoData";
import { useSubscription } from "../context/SubscriptionContext";
import {
  PARENT_BOOKING_HUB_PATH,
  persistParentPostAuthRedirect,
} from "../features/parentFlow/parentFlow";
import { logout } from "../features/auth/user.reducer";
import { getStaticTutorDescription } from "./tutorDescriptions";
import {
  getTutorStatusBadge,
  NO_AVAILABILITY_BADGE,
} from "./tutorAvailabilityStatus";
import { CONTACT_SOCIAL_LINKS } from "../components/Profile/contactSocialLinks";
import {
  getTeacherDisplayName,
  getTeacherIdentifier,
  getTeacherPhotoValue,
  normalizeAvailabilityCollection,
  normalizeTeacherCollection,
  teacherApi,
} from "../features/teacher/teacherApi";
import { formatTeacherYearLabels } from "../utils/teacherYears";
import { normalizeAudPriceForDisplay } from "../utils/audCurrency";
import { resolveApiImageUrl } from "../utils/imageUrl";
import { mergePendingTutorSlotsFromStorage } from "../components/ClassBooking/pendingTutorAvailability";

const locationIcons = [House, Monitor, MapPin];
const socialIconMap = {
  youtube: Youtube,
  instagram: Instagram,
  linkedin: Linkedin,
  facebook: Facebook,
};

const DEFAULT_TUTOR_BADGE = "Available for booking";

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isFutureAvailabilitySlot = (slot, now = new Date()) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
};

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="160" height="160" rx="32" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="58" font-weight="700" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getTeacherYears = (teacher = {}) => {
  const yearSource =
    teacher?.year_labels?.length || typeof teacher?.year_labels === "string"
      ? teacher.year_labels
      : teacher?.year;

  return formatTeacherYearLabels(yearSource);
};

const getTeacherDescription = (teacher = {}) =>
  [
    teacher?.bio,
    teacher?.specializations,
    teacher?.qualifications,
    teacher?.subjects,
    teacher?.about,
  ].find((value) => String(value || "").trim()) || "";

const formatTutorPriceValue = (value) =>
  typeof value === "number" ? `$${value}` : String(value || "On request");

const buildBackendTutorModel = (teacher = {}, descriptionIndex = 0) => {
  const teacherName = getTeacherDisplayName(teacher);
  const years = getTeacherYears(teacher);
  const description = getTeacherDescription(teacher);
  const staticDescription = getStaticTutorDescription(descriptionIndex);
  const rawPrice = teacher?.price_per_session ?? teacher?.price;
  const price = normalizeAudPriceForDisplay(rawPrice);
  const experience = toNumber(teacher?.experience);
  const teacherImage =
    resolveApiImageUrl(getTeacherPhotoValue(teacher)) ||
    buildAvatarDataUri(teacherName);
  const lessonAudience =
    years.length > 0 ? years.join(", ") : "All listed year levels";
  const qualificationText = String(teacher?.qualifications || "").trim();
  const specializationText = String(teacher?.specializations || "").trim();
  const subjectText = String(teacher?.subjects || "").trim();
  const yearsText = years.length ? years.join(", ") : "school maths";
  const formattedPrice =
    typeof price === "number"
      ? `$${new Intl.NumberFormat("en-AU", {
          minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
          maximumFractionDigits: Number.isInteger(price) ? 0 : 2,
        }).format(price)}/h`
      : "Price on request";
  const priceValue = typeof price === "number" ? price : "On request";

  return {
    id: getTeacherIdentifier(teacher) || teacherName.toLowerCase().replace(/\s+/g, "-"),
    name: teacherName,
    image: teacherImage,
    heroText: staticDescription,
    badge: DEFAULT_TUTOR_BADGE,
    years,
    topics: [
      years[0] || "Online tutoring",
      specializationText || "Homework support",
      subjectText || "Maths confidence",
      "Parent-friendly guidance",
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 4),
    lessonLocations: [
      "Online lessons across Australia",
      "Live guided practice during each session",
      "Clear recap and next-step support for families",
    ],
    about: description || staticDescription,
    parentFit:
      specializationText ||
      `A good fit for families looking for steady online maths support in ${yearsText}.`,
    supportIncludes: [
      qualificationText || "Step-by-step explanations",
      subjectText || "Weekly maths practice",
      specializationText || "Confidence building",
      experience !== null
        ? `${experience} year${experience === 1 ? "" : "s"} experience`
        : "Parent-friendly updates",
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 4),
    lessonAudience,
    lessonLanguage: "English",
    lessonOverview: [
      description || staticDescription,
      `Lessons focus on ${yearsText} with guided examples, calm explanations and practice that students can follow step by step.`,
      "Parents can expect clear communication, structured support and an online session format that keeps learning simple and accessible.",
    ],
    price: priceValue,
    packPrices: ["5h pack available on request", "10h pack available on request"],
    onlineRate: formattedPrice,
    freeLesson: "Free lesson availability on request",
    responseStat: "Fast reply",
    students: toNumber(teacher?.students ?? teacher?.student_count) ?? 0,
    location: "Australia",
    mode: "Online",
    level: years.some((year) => Number(String(year).replace(/\D/g, "")) >= 7)
      ? "senior"
      : "primary",
  };
};

export default function TutorInfoDetailPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tutorId } = useParams();
  const { token } = useSelector((state) => state.user);
  const { hasAccess, checkSubscription } = useSubscription();
  const staticTutor = useMemo(() => getTutorById(tutorId), [tutorId]);
  const [savedTutor, setSavedTutor] = useState(false);
  const [similarPage, setSimilarPage] = useState(0);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [bookingGateMessage, setBookingGateMessage] = useState("");
  const [isCheckingBookingAccess, setIsCheckingBookingAccess] = useState(false);
  const tutorDetailQuery = useQuery({
    queryKey: ["public-tutor-detail", tutorId],
    queryFn: () => teacherApi.getTutorDetail(tutorId),
    enabled: Boolean(tutorId) && !staticTutor,
    retry: false,
  });
  const tutorListQuery = useQuery({
    queryKey: ["public-tutor-similar-list"],
    queryFn: () => teacherApi.getAllTeachers(),
    enabled: !staticTutor,
    retry: false,
  });
  const tutorAvailabilityQuery = useQuery({
    queryKey: ["public-tutor-availability", tutorId],
    queryFn: () => teacherApi.getTutorAvailability(tutorId),
    enabled: Boolean(tutorId) && !staticTutor && !tutorDetailQuery.isError,
    retry: false,
  });
  const backendTeachers = useMemo(
    () => normalizeTeacherCollection(tutorListQuery.data),
    [tutorListQuery.data]
  );
  const futureAvailabilitySlots = useMemo(() => {
    if (staticTutor) return [];

    return mergePendingTutorSlotsFromStorage({
      tutorId,
      availabilitySlots: normalizeAvailabilityCollection(
        tutorAvailabilityQuery.data
      ),
    }).filter((slot) => isFutureAvailabilitySlot(slot));
  }, [staticTutor, tutorAvailabilityQuery.data, tutorId]);
  const dynamicTutorDescriptionIndex = useMemo(() => {
    if (staticTutor) return 0;

    const matchedIndex = backendTeachers.findIndex((teacher) => {
      const teacherIdentifier = getTeacherIdentifier(teacher);
      return teacherIdentifier === tutorId;
    });

    return matchedIndex >= 0 ? matchedIndex : 0;
  }, [backendTeachers, staticTutor, tutorId]);

  const dynamicTutor = useMemo(
    () => {
      if (staticTutor || !tutorDetailQuery.data) return null;
      return buildBackendTutorModel(
        tutorDetailQuery.data,
        dynamicTutorDescriptionIndex
      );
    },
    [dynamicTutorDescriptionIndex, staticTutor, tutorDetailQuery.data]
  );
  const tutor = staticTutor || dynamicTutor;
  const isBookingUnavailable =
    !staticTutor &&
    tutorAvailabilityQuery.isSuccess &&
    futureAvailabilitySlots.length === 0;
  const tutorStatusBadge = getTutorStatusBadge({
    badge: tutor?.badge,
    isStaticTutor: Boolean(staticTutor),
    availabilityLoaded: tutorAvailabilityQuery.isSuccess,
    futureAvailabilityCount: futureAvailabilitySlots.length,
  });

  const dynamicSimilarTutors = useMemo(() => {
    if (staticTutor) return [];

    return backendTeachers
      .filter((teacher) => {
        const teacherIdentifier = getTeacherIdentifier(teacher);
        return teacherIdentifier && teacherIdentifier !== tutorId;
      })
      .map((teacher, index) => {
        const teacherIdentifier = getTeacherIdentifier(teacher);
        const originalIndex = backendTeachers.findIndex(
          (candidateTeacher) =>
            getTeacherIdentifier(candidateTeacher) === teacherIdentifier
        );

        return buildBackendTutorModel(
          teacher,
          originalIndex >= 0 ? originalIndex : index
        );
      })
      .slice(0, 12);
  }, [backendTeachers, staticTutor, tutorId]);

  const similarTutors = (staticTutor
    ? TUTORS.filter((candidateTutor) => candidateTutor.id !== tutor.id).sort(
        (firstTutor, secondTutor) => {
          if (firstTutor.level === tutor.level && secondTutor.level !== tutor.level) return -1;
          if (firstTutor.level !== tutor.level && secondTutor.level === tutor.level) return 1;
          return 0;
        }
      )
    : dynamicSimilarTutors);
  const similarTutorsPerPage = 4;
  const totalSimilarPages = Math.max(
    1,
    Math.ceil(similarTutors.length / similarTutorsPerPage)
  );
  const visibleSimilarTutors = similarTutors.slice(
    similarPage * similarTutorsPerPage,
    similarPage * similarTutorsPerPage + similarTutorsPerPage
  );

  if (!tutor && tutorDetailQuery.isLoading) {
    return (
      <div className="min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-10 sm:px-6 lg:px-8">
          <div className="rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-16 text-center text-lg font-medium text-[#475569]">
            Loading tutor profile...
          </div>
        </div>
      </div>
    );
  }

  if (!tutor && tutorDetailQuery.isError) {
    return (
      <div className="min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-10 sm:px-6 lg:px-8">
          <div className="rounded-[34px] border border-[#f1c8bf] bg-[#fff7f5] px-6 py-16 text-center">
            <p className="text-xl font-medium text-[#f97316]">
              We couldn't load this tutor profile.
            </p>
            <p className="mt-3 text-base text-[#475569]">
              {tutorDetailQuery.error?.message || "Please try again shortly."}
            </p>
            <Link
              to="/tutorinfo"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#FF5E47] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
            >
              Back to tutors
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const shareUrl = window.location.href;

    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch (error) {
        console.log("Unable to copy tutor profile link:", error);
      }
    }
  };

  const handleParentAuthNavigation = (path) => {
    persistParentPostAuthRedirect(PARENT_BOOKING_HUB_PATH);
    navigate(path, {
      state: {
        redirectTo: PARENT_BOOKING_HUB_PATH,
      },
    });
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/tutorinfo", { replace: true });
  };

  const handleBookSession = async () => {
    setBookingGateMessage("");
    persistParentPostAuthRedirect(PARENT_BOOKING_HUB_PATH);

    if (!token) {
      setShowAuthPrompt(true);
      return;
    }

    let canBook = hasAccess;
    setIsCheckingBookingAccess(true);

    if (!canBook) {
      try {
        const latestSubscription = await checkSubscription();
        const latestStatus = latestSubscription?.subscription_status;
        const requiresPaymentMethod = Boolean(
          latestSubscription?.requires_payment_method
        );

        canBook =
          Boolean(latestSubscription?.has_access) ||
          ((latestStatus === "active" || latestStatus === "trialing") &&
            !requiresPaymentMethod);
      } catch (_error) {
        setBookingGateMessage(
          "We couldn't verify the student's subscription right now. Please try again in a moment."
        );
        setIsCheckingBookingAccess(false);
        return;
      }
    }

    setIsCheckingBookingAccess(false);

    if (canBook) {
      navigate(PARENT_BOOKING_HUB_PATH);
      return;
    }

    navigate("/subscription", {
      state: {
        from: PARENT_BOOKING_HUB_PATH,
        source: "parent-booking",
      },
    });
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
      <div className="mx-auto max-w-7xl px-4 pb-12 pt-6 sm:px-6 lg:px-8">
        <header className="border-b border-[#ddd3c6] pb-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <Link className="flex items-center gap-3" to="/tutorinfo">
              <img
                src={GLOBAL_IMAGES.dingo}
                alt="MathDingo"
                className="h-12 w-12 rounded-full bg-white p-2"
              />
              <img
                src={GLOBAL_IMAGES.dingoLogoText}
                alt="MathDingo"
                className="h-8 w-auto object-contain sm:h-9"
              />
            </Link>

            <div className="flex flex-wrap items-center gap-3 xl:justify-end">
              <Link
                to="/contact"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF5E47] bg-white text-[#FF5E47]"
                aria-label="Contact support"
              >
                <CircleHelp size={18} />
              </Link>
              <Link
                to="/teachersignup"
                className="rounded-2xl border border-[#FF5E47] bg-white px-5 py-2.5 text-sm font-medium text-[#FF5E47] sm:text-base"
              >
                Become a Tutor
              </Link>
              {token ? (
                <>
                  <Link
                    to="/parent/classBooking/myBookings"
                    className="rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                  >
                    My Bookings
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-2xl border border-[#FF5E47] bg-white px-5 py-2.5 text-sm font-medium text-[#FF5E47] transition hover:bg-[#fff4f1] sm:text-base"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/parent/login"
                  className="rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                >
                  Parent Log In
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="pt-8">
          <Link
            to="/tutorinfo"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#FF5E47] transition hover:opacity-80"
          >
            <ArrowLeft size={18} />
            Back to tutors
          </Link>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_292px] lg:items-start">
            <section>
              <div className="flex flex-wrap gap-3">
                {tutor.topics.map((topic) => (
                  <span
                    key={`${tutor.id}-${topic}`}
                    className="rounded-full bg-[#fff0ec] px-4 py-2 text-sm font-medium text-[#ff6b61]"
                  >
                    {topic}
                  </span>
                ))}
              </div>

              <h1 className="mt-8 max-w-4xl text-2xl font-medium leading-tight text-[#111827] sm:text-3xl lg:text-[36px] lg:leading-[1.15]">
                {tutor.heroText}
              </h1>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm font-medium text-[#475569] sm:text-base">
                <span className="inline-flex items-center gap-2">
                  <Sparkles size={18} className="text-[#FF5E47]" />
                  {tutorStatusBadge}
                </span>
                <span className="inline-flex items-center gap-2">
                  <BookOpen size={18} className="text-[#FF5E47]" />
                  {tutor.years.join(", ")}
                </span>
              </div>

              <div className="mt-14">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  Lesson location
                </h2>
                <div className="mt-6 flex flex-col gap-4">
                  {tutor.lessonLocations.map((location, index) => {
                    const LocationIcon =
                      locationIcons[index] || locationIcons[locationIcons.length - 1];

                    return (
                      <div
                        key={`${tutor.id}-${location}`}
                        className="inline-flex w-fit max-w-full items-center gap-3 rounded-full border border-[#ddd3c6] bg-white px-4 py-3 text-sm font-medium text-[#1f2937] sm:text-base"
                      >
                        <LocationIcon size={20} className="shrink-0 text-[#FF5E47]" />
                        <span>{location}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-16">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  About {tutor.name}
                </h2>
                <p className="mt-6 max-w-4xl text-lg leading-9 text-[#374151] sm:text-xl">
                  {tutor.about}
                </p>
              </div>

              <div className="mt-16">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  Best fit for MathDingo families
                </h2>
                <p className="mt-6 max-w-4xl text-lg leading-9 text-[#374151] sm:text-xl">
                  {tutor.parentFit}
                </p>
              </div>

              <div className="mt-16">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  What parents can expect
                </h2>
                <div className="mt-6 flex flex-wrap gap-3">
                  {tutor.supportIncludes.map((item) => (
                    <span
                      key={`${tutor.id}-${item}`}
                      className="rounded-full border border-[#d8cfbf] bg-white px-4 py-2.5 text-sm font-medium text-[#FF5E47] sm:text-base"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-16 border-t border-[#ddd3c6] pt-12">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  About the lesson
                </h2>

                <div className="mt-6 flex flex-wrap gap-4">
                  <div className="inline-flex w-fit max-w-full items-center gap-3 rounded-full border border-[#ddd3c6] bg-white px-4 py-3 text-sm font-medium text-[#1f2937] sm:text-base">
                    <BookOpen size={20} className="shrink-0 text-[#FF5E47]" />
                    <span>{tutor.lessonAudience}</span>
                  </div>
                  <div className="inline-flex w-fit max-w-full items-center gap-3 rounded-full border border-[#ddd3c6] bg-white px-4 py-3 text-sm font-medium text-[#1f2937] sm:text-base">
                    <Flag size={20} className="shrink-0 text-[#FF5E47]" />
                    <span>{tutor.lessonLanguage}</span>
                  </div>
                </div>

                <h3 className="mt-10 text-xl font-medium text-[#111827] sm:text-2xl">
                  What you&apos;ll do and how we learn
                </h3>

                <div className="mt-6 max-w-5xl space-y-8 text-lg leading-9 text-[#374151] sm:text-xl">
                  {tutor.lessonOverview.map((paragraph) => (
                    <p key={`${tutor.id}-${paragraph}`}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div className="mt-16">
                <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                  Rates
                </h2>

                <div className="mt-6 rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-8">
                  <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <p className="text-xl font-medium text-[#111827]">Price</p>
                      <p className="mt-4 text-2xl font-medium text-[#111827]">
                        {formatTutorPriceValue(tutor.price)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xl font-medium text-[#111827]">
                        Pack prices
                      </p>
                      <div className="mt-4 space-y-3 text-xl font-medium text-[#111827]">
                        {tutor.packPrices.map((pack) => (
                          <p key={`${tutor.id}-${pack}`}>{pack}</p>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xl font-medium text-[#111827]">Online</p>
                      <p className="mt-4 text-2xl font-medium text-[#111827]">
                        {tutor.onlineRate}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-medium text-[#111827]">
                          Free lessons
                        </p>
                        <Info size={18} className="text-[#FF5E47]" />
                      </div>
                      <p className="mt-4 text-2xl font-medium text-[#111827]">
                        {tutor.freeLesson}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-16 pb-2">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-2xl font-medium text-[#111827] sm:text-3xl">
                    Similar maths tutors in Australia
                  </h2>

                  <div className="flex items-center gap-3 self-start sm:self-auto">
                    <span className="text-lg font-medium text-[#111827]">
                      {Math.min(similarPage + 1, totalSimilarPages)}/{totalSimilarPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setSimilarPage((currentPage) => Math.max(0, currentPage - 1))
                      }
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#FF5E47] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={similarPage === 0}
                      aria-label="Previous similar tutors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSimilarPage((currentPage) =>
                          Math.min(totalSimilarPages - 1, currentPage + 1)
                        )
                      }
                      className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#FF5E47] shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45"
                      disabled={similarPage >= totalSimilarPages - 1}
                      aria-label="Next similar tutors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                  {visibleSimilarTutors.map((similarTutor) => (
                    <article
                      key={similarTutor.id}
                      className="overflow-hidden rounded-[28px] bg-white shadow-[0_18px_45px_rgba(0,0,0,0.06)]"
                    >
                      <Link
                        to={`/tutorinfo/${similarTutor.id}`}
                        className="group block"
                      >
                        <div className="relative">
                          <img
                            src={similarTutor.image}
                            alt={similarTutor.name}
                            className="h-[280px] w-full object-cover transition duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                            <h3 className="text-2xl font-medium">{similarTutor.name}</h3>
                            <p className="mt-2 text-sm font-medium text-white/90">
                              {similarTutor.location} • {similarTutor.mode}
                            </p>
                          </div>
                        </div>
                      </Link>

                      <div className="px-5 pb-5 pt-4">
                        <div className="flex flex-wrap gap-3">
                          <span className="rounded-full bg-[#fff0ec] px-4 py-2 text-sm font-medium text-[#ff6b61]">
                            {typeof similarTutor.price === "number"
                              ? `${formatTutorPriceValue(similarTutor.price)}/h`
                              : formatTutorPriceValue(similarTutor.price)}
                          </span>
                          <span className="rounded-full bg-[#fff0ec] px-4 py-2 text-sm font-medium text-[#ff6b61]">
                            {similarTutor.badge}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>

            </section>

            <aside className="lg:sticky lg:top-6">
              <div className="rounded-[30px] bg-white p-4 shadow-[0_20px_48px_rgba(0,0,0,0.08)]">
                <div className="flex justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={() => setSavedTutor((currentValue) => !currentValue)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#ddd3c6] text-[#FF5E47]"
                    aria-label={savedTutor ? "Remove saved tutor" : "Save tutor"}
                  >
                    <Heart
                      size={18}
                      className={savedTutor ? "fill-[#FF5E47] text-[#FF5E47]" : ""}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#ddd3c6] text-[#FF5E47]"
                    aria-label="Share tutor profile"
                  >
                    <Share2 size={17} />
                  </button>
                </div>

                <div className="mt-1 flex flex-col items-center text-center">
                  <div className="relative">
                    <img
                      src={tutor.image}
                      alt={tutor.name}
                      className="h-36 w-36 rounded-[28px] object-cover sm:h-40 sm:w-40"
                    />
                    <div className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-white bg-[#fff0ec] text-[#FF5E47]">
                      <Sparkles size={16} />
                    </div>
                  </div>

                  <h2 className="mt-4 text-[28px] font-medium leading-none text-[#111827]">
                    {tutor.name}
                  </h2>
                  <p className="mt-2 text-sm font-medium text-[#475569]">
                    {tutor.location}
                  </p>
                </div>

                <div className="mt-6 space-y-4 text-sm sm:text-base">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-[#374151]">Price</span>
                    <span className="text-xl font-medium text-[#111827]">
                      {formatTutorPriceValue(tutor.price)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-2 font-medium text-[#374151]">
                      <Clock3 size={16} className="text-[#FF5E47]" />
                      Answer
                    </span>
                    <span className="text-lg font-medium text-[#111827]">
                      {tutor.responseStat}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-2 font-medium text-[#374151]">
                      <Users size={16} className="text-[#FF5E47]" />
                      Students
                    </span>
                    <span className="text-lg font-medium text-[#111827]">
                      {tutor.students}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBookSession}
                  disabled={isCheckingBookingAccess || isBookingUnavailable}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-[#FF5E47] px-6 py-3 text-sm font-medium text-white transition hover:bg-[#e64d37] disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
                >
                  {isCheckingBookingAccess
                    ? "Checking access..."
                    : isBookingUnavailable
                      ? NO_AVAILABILITY_BADGE
                      : "Book Session"}
                </button>

                {!isBookingUnavailable ? (
                  <p className="mt-3 text-center text-base font-medium text-[#ff6b61]">
                    {tutorStatusBadge}
                  </p>
                ) : null}
                <p className="mt-2 text-center text-xs font-medium leading-5 text-[#64748b]">
                  Parent account required before booking a MathDingo tutoring session.
                </p>
                {bookingGateMessage ? (
                  <div className="mt-4 rounded-[24px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {bookingGateMessage}
                  </div>
                ) : null}
              </div>
            </aside>
          </div>

          {showAuthPrompt ? (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#111827]/40 px-4 backdrop-blur-sm">
              <div className="relative w-full max-w-md rounded-[32px] border border-[#ddd3c6] bg-white p-6 shadow-[0_24px_70px_rgba(11,43,102,0.18)]">
                <button
                  type="button"
                  onClick={() => setShowAuthPrompt(false)}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#e5ddd0] text-[#FF5E47]"
                  aria-label="Close login prompt"
                >
                  <X size={18} />
                </button>

                <span className="inline-flex rounded-full bg-[#fff0ec] px-4 py-2 text-sm font-medium text-[#ff6b61]">
                  Parent booking required
                </span>
                <h2 className="mt-5 text-3xl font-medium text-[#f97316]">
                  You&apos;re not logged in
                </h2>
                <p className="mt-3 text-base leading-7 text-[#475569]">
                  Please log in with the student&apos;s existing credentials, or
                  create a parent account first. We&apos;ll resume the booking
                  flow as soon as you&apos;re authenticated.
                </p>

                <div className="mt-8 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => handleParentAuthNavigation("/parent/login")}
                    className="inline-flex items-center justify-center rounded-2xl bg-[#FF5E47] px-5 py-3 text-base font-medium text-white transition hover:bg-[#e64d37]"
                  >
                    Parent Login
                  </button>
                  <button
                    type="button"
                    onClick={() => handleParentAuthNavigation("/parent/signup")}
                    className="inline-flex items-center justify-center rounded-2xl border border-[#FF5E47] bg-white px-5 py-3 text-base font-medium text-[#FF5E47] transition hover:bg-[#fff4f1]"
                  >
                    Create Parent Account
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <footer className="mt-20 border-y border-[#ddd3c6] py-14">
            <div className="grid gap-y-12 md:grid-cols-2 md:gap-x-16 lg:grid-cols-4 lg:gap-x-12 xl:gap-x-20">
                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">About</h3>
                  <div className="mt-6 space-y-5 text-lg font-medium text-[#111827]">
                    <Link
                      className="block transition-colors hover:text-[#FF5E47]"
                      to="/about"
                    >
                      About MathDingo
                    </Link>
                    <Link
                      className="block transition-colors hover:text-[#FF5E47]"
                      to={token ? "/tutorinfo" : "/parent/login"}
                    >
                      Parent account
                    </Link>
                    {token ? (
                      <Link
                        className="block transition-colors hover:text-[#FF5E47]"
                        to="/parent/classBooking/myBookings"
                      >
                        My bookings
                      </Link>
                    ) : null}
                    <Link className="block transition-colors hover:text-[#FF5E47]" to="/teachersignup">
                      Become a tutor
                    </Link>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">
                    Learning areas
                  </h3>
                  <div className="mt-6 space-y-5 text-lg font-medium text-[#111827]">
                    {[
                      "Maths",
                      "Primary numeracy",
                      "Fractions",
                      "Algebra",
                      "Problem solving",
                      "Homework help",
                      "Exam preparation",
                      "Online tutoring",
                    ].map((item) => (
                      <span key={item} className="block">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">Cities</h3>
                  <div className="mt-6 space-y-5 text-lg font-medium text-[#111827]">
                    {[
                      "Sydney",
                      "Melbourne",
                      "Brisbane",
                      "Perth",
                      "Adelaide",
                      "Canberra",
                      "Geelong",
                      "All cities",
                    ].map((city) => (
                      <span key={city} className="block">
                        {city}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-medium text-[#111827]">
                    Help & support
                  </h3>
                  <div className="mt-6 space-y-5 text-lg font-medium text-[#111827]">
                    <Link className="block transition-colors hover:text-[#FF5E47]" to="/contact">
                      Contact us
                    </Link>
                  </div>
                </div>
              </div>

            <div className="mt-12 border-t border-[#ddd3c6]">
              <div className="flex flex-col gap-5 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xl font-medium text-[#111827]">
                  © 2026 MathDingo, learn with confidence!
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  {CONTACT_SOCIAL_LINKS.map(({ id, label, href }) => {
                    const Icon = socialIconMap[id];

                    return (
                      <a
                        key={id}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#ffd9d2] bg-white text-[#FF5E47] shadow-[0_10px_24px_rgba(255,94,71,0.12)] transition hover:-translate-y-0.5 hover:border-[#FF5E47] hover:bg-[#fff4f1]"
                      >
                        <Icon size={20} strokeWidth={2.1} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
